/**
 * Carousel initialization script for GrapesJS-generated carousels
 * Handles dot navigation clicks and autoplay functionality
 */

interface CarouselState {
  currentSlide: number
  intervalId: ReturnType<typeof setInterval> | null
  slides: HTMLElement[]
  dots: HTMLElement[]
  track: HTMLElement
  // Touch state
  touchStartX: number
  touchStartY: number
  isDragging: boolean
}

const carouselStates = new WeakMap<HTMLElement, CarouselState>()

function updateCarousel(carousel: HTMLElement, slideIndex: number) {
  const state = carouselStates.get(carousel)
  if (!state) return

  // Clamp to valid range
  const newIndex = Math.max(0, Math.min(slideIndex, state.slides.length - 1))
  state.currentSlide = newIndex

  // Update track position
  state.track.style.transform = `translateX(-${newIndex * 100}%)`

  // Update dots styling
  state.dots.forEach((dot, index) => {
    if (index === newIndex) {
      dot.style.background = 'white'
    } else {
      dot.style.background = 'rgba(255,255,255,0.5)'
    }
  })
}

function goToNextSlide(carousel: HTMLElement) {
  const state = carouselStates.get(carousel)
  if (!state) return

  const nextIndex = (state.currentSlide + 1) % state.slides.length
  updateCarousel(carousel, nextIndex)
}

function startAutoplay(carousel: HTMLElement) {
  const state = carouselStates.get(carousel)
  if (!state) return

  // Stop any existing interval
  if (state.intervalId) {
    clearInterval(state.intervalId)
  }

  const interval = parseInt(carousel.dataset.interval || '10000', 10)
  state.intervalId = setInterval(() => {
    goToNextSlide(carousel)
  }, interval)
}

function stopAutoplay(carousel: HTMLElement) {
  const state = carouselStates.get(carousel)
  if (!state || !state.intervalId) return

  clearInterval(state.intervalId)
  state.intervalId = null
}

function initCarousel(carousel: HTMLElement) {
  // Skip if already initialized
  if (carouselStates.has(carousel)) return

  const track = carousel.querySelector('[data-carousel-track]') as HTMLElement
  const slides = Array.from(carousel.querySelectorAll('[data-carousel-slide]')) as HTMLElement[]
  const dots = Array.from(carousel.querySelectorAll('[data-carousel-dot]')) as HTMLElement[]

  if (!track || slides.length === 0) return

  // Initialize state
  const state: CarouselState = {
    currentSlide: 0,
    intervalId: null,
    slides,
    dots,
    track,
    touchStartX: 0,
    touchStartY: 0,
    isDragging: false,
  }
  carouselStates.set(carousel, state)

  // Add click handlers to dots
  dots.forEach((dot, index) => {
    dot.style.cursor = 'pointer'
    dot.style.pointerEvents = 'auto'
    dot.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      updateCarousel(carousel, index)
      // Reset autoplay timer on manual navigation
      if (carousel.dataset.autoplay === 'true') {
        stopAutoplay(carousel)
        startAutoplay(carousel)
      }
    })
  })

  // Touch/Swipe support
  const SWIPE_THRESHOLD = 50

  function handleTouchStart(e: TouchEvent) {
    const state = carouselStates.get(carousel)
    if (!state) return

    state.touchStartX = e.touches[0].clientX
    state.touchStartY = e.touches[0].clientY
    state.isDragging = true
    stopAutoplay(carousel)
  }

  function handleTouchMove(e: TouchEvent) {
    const state = carouselStates.get(carousel)
    if (!state || !state.isDragging) return

    const deltaX = e.touches[0].clientX - state.touchStartX
    const deltaY = e.touches[0].clientY - state.touchStartY

    // If horizontal swipe is dominant, prevent vertical scroll
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      e.preventDefault()
    }
  }

  function handleTouchEnd(e: TouchEvent) {
    const state = carouselStates.get(carousel)
    if (!state || !state.isDragging) return

    const deltaX = e.changedTouches[0].clientX - state.touchStartX
    state.isDragging = false

    if (Math.abs(deltaX) > SWIPE_THRESHOLD) {
      if (deltaX > 0) {
        // Swipe right - go to previous slide
        updateCarousel(carousel, state.currentSlide - 1)
      } else {
        // Swipe left - go to next slide
        updateCarousel(carousel, state.currentSlide + 1)
      }
    }

    // Restart autoplay if enabled
    if (carousel.dataset.autoplay === 'true') {
      startAutoplay(carousel)
    }
  }

  // Mouse drag support (for trackpad/desktop)
  function handleMouseDown(e: MouseEvent) {
    const state = carouselStates.get(carousel)
    if (!state) return

    // Don't start drag if clicking on dots
    if ((e.target as HTMLElement).hasAttribute('data-carousel-dot')) return

    state.touchStartX = e.clientX
    state.touchStartY = e.clientY
    state.isDragging = true
    stopAutoplay(carousel)

    // Prevent text selection during drag
    e.preventDefault()
  }

  function handleMouseMove(e: MouseEvent) {
    const state = carouselStates.get(carousel)
    if (!state || !state.isDragging) return
    e.preventDefault()
  }

  function handleMouseUp(e: MouseEvent) {
    const state = carouselStates.get(carousel)
    if (!state || !state.isDragging) return

    const deltaX = e.clientX - state.touchStartX
    state.isDragging = false

    if (Math.abs(deltaX) > SWIPE_THRESHOLD) {
      if (deltaX > 0) {
        // Drag right - go to previous slide
        updateCarousel(carousel, state.currentSlide - 1)
      } else {
        // Drag left - go to next slide
        updateCarousel(carousel, state.currentSlide + 1)
      }
    }

    // Restart autoplay if enabled
    if (carousel.dataset.autoplay === 'true') {
      startAutoplay(carousel)
    }
  }

  function handleMouseLeave() {
    const state = carouselStates.get(carousel)
    if (!state) return

    if (state.isDragging) {
      state.isDragging = false
      // Restart autoplay if enabled
      if (carousel.dataset.autoplay === 'true') {
        startAutoplay(carousel)
      }
    }
  }

  // Add touch event listeners
  carousel.addEventListener('touchstart', handleTouchStart, { passive: true })
  carousel.addEventListener('touchmove', handleTouchMove, { passive: false })
  carousel.addEventListener('touchend', handleTouchEnd, { passive: true })

  // Add mouse event listeners for trackpad drag
  carousel.addEventListener('mousedown', handleMouseDown)
  carousel.addEventListener('mousemove', handleMouseMove)
  carousel.addEventListener('mouseup', handleMouseUp)
  carousel.addEventListener('mouseleave', handleMouseLeave)

  // Pause autoplay on hover (only if not dragging)
  carousel.addEventListener('mouseenter', () => {
    stopAutoplay(carousel)
  })

  carousel.addEventListener('mouseleave', () => {
    if (carousel.dataset.autoplay === 'true') {
      startAutoplay(carousel)
    }
  })

  // Start autoplay if enabled
  if (carousel.dataset.autoplay === 'true') {
    startAutoplay(carousel)
  }

  // Set initial state
  updateCarousel(carousel, 0)
}

function destroyCarousel(carousel: HTMLElement) {
  stopAutoplay(carousel)
  carouselStates.delete(carousel)
}

/**
 * Initialize all carousels on the page
 */
export function initCarousels() {
  const carousels = document.querySelectorAll('[data-carousel]') as NodeListOf<HTMLElement>
  carousels.forEach(initCarousel)
}

/**
 * Cleanup all carousels (useful for SPA navigation)
 */
export function destroyCarousels() {
  const carousels = document.querySelectorAll('[data-carousel]') as NodeListOf<HTMLElement>
  carousels.forEach(destroyCarousel)
}

/**
 * Setup a MutationObserver to auto-initialize carousels when they're added to the DOM
 */
export function setupCarouselObserver() {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Check added nodes for carousels
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement) {
          // Check if the node itself is a carousel
          if (node.hasAttribute('data-carousel')) {
            initCarousel(node)
          }
          // Check children for carousels
          const childCarousels = node.querySelectorAll('[data-carousel]') as NodeListOf<HTMLElement>
          childCarousels.forEach(initCarousel)
        }
      })

      // Cleanup removed carousels
      mutation.removedNodes.forEach((node) => {
        if (node instanceof HTMLElement) {
          if (node.hasAttribute('data-carousel')) {
            destroyCarousel(node)
          }
          const childCarousels = node.querySelectorAll('[data-carousel]') as NodeListOf<HTMLElement>
          childCarousels.forEach(destroyCarousel)
        }
      })
    }
  })

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  })

  return observer
}

// Auto-initialize on DOM ready
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initCarousels()
      setupCarouselObserver()
    })
  } else {
    // DOM is already ready
    initCarousels()
    setupCarouselObserver()
  }
}
